/**
 * Les 9 rôles de la bible (§4.1) — SOURCE UNIQUE, partagée web/BFF.
 * §4.1 exige : PLUSIEURS rôles par utilisateur (E9) et des exceptions
 * nominatives exprimées par des PERMISSIONS auditables — jamais un
 * `if (email === …)` dispersé, jamais un `role === 'admin'` en dur dans un
 * composant : passer par les helpers nommés ci-dessous.
 *
 * `super_admin` (Dove, 28/08) est ADDITIF : son porteur garde AUSSI `admin`.
 * Jamais de remap `admin` → `super_admin` — trois checks `admin` en dur
 * (garde-admin.ts:32, gmail/connector.ts:115, notes.ts:96) casseraient.
 */
export const ROLES = [
  "super_admin", // Dove seul — décisions structurantes réservées (28/08)
  "admin", // Direction / Super-admin — tout voir, tout configurer
  "secretariat", // Administration — clients, communications, tâches, documents
  "commercial", // Relation praticiens — prospects, campagnes, opportunités
  "production", // Responsable production — fiches, cas, incidents
  "technicien", // Technicien / céramiste — contexte technique, finance limitée
  "comptabilite", // Facturation et recouvrement
  "logistique", // Ramassages et livraisons
  "lecture", // Lecture seule / audit
] as const;

export type Role = (typeof ROLES)[number];

export function isRole(value: string): value is Role {
  return (ROLES as readonly string[]).includes(value);
}

/**
 * Permissions nommées (§4.1/§4.2) — exceptions nominatives auditables,
 * attribuées dans l'allowlist versionnée du BFF (git log = audit).
 */
export const PERMISSIONS = [
  "arbitrage", // Estelle : arbitrage relationnel, escalades J+30, validation statuts sensibles
  "publication", // §8.6 : publier/archiver des documents (Estelle SI Dove l'accorde — non accordée par défaut)
] as const;
export type Permission = (typeof PERMISSIONS)[number];

export function hasRole(roles: readonly Role[], ...voulus: readonly Role[]): boolean {
  return roles.some((r) => voulus.includes(r));
}

/** Réservé au porteur du rôle `super_admin` (Dove) — `admin` seul ne suffit pas. */
export function isSuperAdmin(roles: readonly Role[]): boolean {
  return roles.includes("super_admin");
}

/** Rôles autorisés à voir les données financières (bible §2.1 + §30.14). */
export const ROLES_FINANCE: readonly Role[] = ["admin", "secretariat", "comptabilite"];

export function canSeeFinance(roles: readonly Role[]): boolean {
  return hasRole(roles, ...ROLES_FINANCE);
}

/** Rôles d'encadrement (équivalent isStaffRole de Courses, sans le piège super_admin). */
export const ROLES_STAFF: readonly Role[] = ["admin", "secretariat", "commercial", "production"];

export function isStaff(roles: readonly Role[]): boolean {
  return hasRole(roles, ...ROLES_STAFF);
}

/**
 * LES ADRESSES DE L'ÉQUIPE — la seule source, partagée serveur ET écran.
 *
 * Le 16/09, Estelle a été refusée à la porte du CRM parce que l'allowlist
 * portait `estelle.geceram@gmail.com` alors que son compte Google est
 * `estelle.chaouat@gmail.com` — et la même adresse fausse vivait en dur dans
 * trois autres fichiers du serveur, plus une côté écran. Une adresse changée
 * ici change partout ; `roles.test.ts` (bff) vérifie que les clés de
 * `ROLE_MAP` sont exactement celles-ci, en minuscules.
 */
export const EMAILS_EQUIPE = {
  dove: "dove.chaouat@gmail.com",
  elodie: "elodie.geceram@gmail.com",
  estelle: "estelle.chaouat@gmail.com",
  cfao: "cfao.geceram@gmail.com",
} as const;

export type EmailEquipe = (typeof EMAILS_EQUIPE)[keyof typeof EMAILS_EQUIPE];

/**
 * Le prénom d'affichage de chaque compte, avec ses accents. Dérivé de
 * l'adresse, « estelle.chaouat » donnerait « Estelle Chaouat » et
 * « elodie.geceram » donnerait « Elodie » sans accent : ce qu'on affiche à
 * l'écran et dans les titres de notes se décide ici, pas par une heuristique.
 */
export const PRENOMS_EQUIPE: Readonly<Record<EmailEquipe, string>> = {
  [EMAILS_EQUIPE.dove]: "Dove",
  [EMAILS_EQUIPE.elodie]: "Élodie",
  [EMAILS_EQUIPE.estelle]: "Estelle",
  [EMAILS_EQUIPE.cfao]: "CFAO",
};
